import { zodResolver } from "@hookform/resolvers/zod";
import {
  type MarketSettings,
  type SaveMarketSettings,
  type Warehouse,
  WAREHOUSE_KIND_LABELS,
  WAREHOUSE_KINDS,
  type WarehouseKind,
  WRITE_OFF_LOSS_KEYS,
  WRITE_OFF_REASON_LABELS,
  WRITE_OFF_REASONS,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Dialog,
  FormField,
  Input,
  MoneyInput,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Star } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useState } from "react";
import { type FieldErrors, type Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCompanyStations } from "../../../features/accounting/shared.js";
import { Th } from "../../../features/fuel/shared.js";
import {
  type MarketSettingsFormValues,
  marketSettingsFormFrom,
  marketSettingsFormSchema,
} from "../../../features/market/market-values.js";
import {
  useMarketPermissions,
  useMarketSettings,
  useRefreshMarket,
  useWarehouses,
} from "../../../features/market/queries.js";
import { MarketPage, marketSearchShape, TableFrame } from "../../../features/market/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";

const TABS = ["settings", "warehouses"] as const;

const searchSchema = z.object({
  ...marketSearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
  /** "company" ya da bir istasyon kimliği: ayar satırı ŞİRKET ya da İSTASYON geçersiz kılmasıdır. */
  scope: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/market/ayarlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: MarketSettingsRoute,
});

/**
 * Market ayarları (Prompt 16 — web). İki sekme:
 *  · **Ayarlar** — şirket satırı + istasyon geçersiz kılması (kasa / akaryakıt ayarlarıyla AYNI kalıp); hiçbir eşik
 *    koda gömülmez ve değerler "doğrulanmalı" gelir. Kayıt yoksa ürün varsayılanları gösterilir (hedef sürüm 0).
 *  · **Depolar** — istasyonun depoları (varsayılan depo kasa satışının düştüğü depodur), ekle / düzenle /
 *    varsayılan yap / pasife al.
 * Ekran OKUMAYA `market.read` ile açılır; ayar kaydı `market.settings.manage`, depo `market.catalog.manage` ister.
 */
function MarketSettingsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketSettings")}
      description={t("market.settings.description")}
      breadcrumb={[{ label: t("menu.marketSettings") }]}
    >
      {({ companyId }) => <SettingsBody companyId={companyId} />}
    </MarketPage>
  );
}

function SettingsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const tab = search.tab ?? "settings";
  return (
    <Tabs
      className="grid gap-4"
      value={tab}
      onValueChange={(next) =>
        void navigate({
          search: (previous) => ({ ...previous, tab: next as (typeof TABS)[number] }),
          replace: true,
        })
      }
    >
      <TabsList aria-label={t("market.settings.tabsLabel")}>
        {TABS.map((value) => (
          <TabsTrigger key={value} value={value}>
            {t(`market.settings.tab.${value}`)}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value={tab} className="grid gap-4 pt-0">
        {tab === "settings" ? <SettingsTab companyId={companyId} /> : <WarehousesTab companyId={companyId} />}
      </TabsContent>
    </Tabs>
  );
}

function ScopePicker({
  companyId,
  value,
  onChange,
  allowCompany,
  label,
  hint,
}: {
  companyId: string;
  value: string;
  onChange: (value: string) => void;
  allowCompany: boolean;
  label: string;
  hint?: string;
}): ReactElement {
  const { t } = useTranslation();
  const stations = useCompanyStations(companyId);
  return (
    <Card className="flex flex-wrap items-end gap-3 p-4">
      <FormField label={label} {...(hint ? { description: hint } : {})} className="w-72">
        {(control) => (
          <Select {...control} value={value} onChange={(event) => onChange(event.target.value)}>
            {allowCompany ? <option value="company">{t("market.settings.companyScope")}</option> : null}
            {(stations.data ?? []).map((station) => (
              <option key={station.id} value={station.id}>
                {station.code} {station.name}
              </option>
            ))}
          </Select>
        )}
      </FormField>
    </Card>
  );
}

// ── Ayarlar ──────────────────────────────────────────────────────────────────
function SettingsTab({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const scope = search.scope ?? "company";
  const scopeStationId = scope === "company" ? null : scope;
  const settings = useMarketSettings(companyId, scopeStationId);

  return (
    <>
      <ScopePicker
        companyId={companyId}
        value={scope}
        allowCompany
        label={t("market.settings.scope")}
        hint={t("market.settings.scopeHint")}
        onChange={(next) =>
          void navigate({ search: (previous) => ({ ...previous, scope: next }), replace: true })
        }
      />
      {settings.error ? (
        <Alert tone="danger">{errorMessage(settings.error)}</Alert>
      ) : !settings.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <SettingsForm
          key={`${companyId}:${scope}`}
          settings={settings.data as MarketSettings}
          scopeStationId={scopeStationId}
          onReload={() => void settings.refetch()}
        />
      )}
    </>
  );
}

/** Yuva yollu (`a.b`) alan hatası: hata her zaman ilgili alanın altında görünmelidir. */
function errorAt(errors: FieldErrors<MarketSettingsFormValues>, name: string): string | undefined {
  const node = name
    .split(".")
    .reduce<unknown>(
      (current, part) => (current as Record<string, unknown> | undefined)?.[part],
      errors as unknown,
    );
  const message = (node as { message?: unknown } | undefined)?.message;
  return typeof message === "string" ? message : undefined;
}

function SettingsForm({
  settings,
  scopeStationId,
  onReload,
}: {
  settings: MarketSettings;
  scopeStationId: string | null;
  onReload: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const canManage = useMarketPermissions().canManageSettings;
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  // İstasyon kapsamında henüz geçersiz kılma yoksa şirket satırının değerleri gösterilir; hedef sürüm 0'dır.
  const overrideExists = scopeStationId !== null && settings.stationId === scopeStationId;
  const isDefaultRow = scopeStationId === null ? settings.isDefault : !overrideExists;
  const targetVersion = scopeStationId === null ? settings.version : overrideExists ? settings.version : 0;

  const form = useForm<MarketSettingsFormValues, unknown, SaveMarketSettings>({
    resolver: zodResolver(marketSettingsFormSchema),
    defaultValues: marketSettingsFormFrom(settings, { stationId: scopeStationId, version: targetVersion }),
  });
  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = form;

  useEffect(() => {
    form.reset(marketSettingsFormFrom(settings, { stationId: scopeStationId, version: targetVersion }));
    // Sunucudan yeni ayar geldiğinde (yenile / kaydet sonrası) form tazelenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, scopeStationId, targetVersion]);

  const save = useMutation({
    mutationFn: async (body: SaveMarketSettings) =>
      (await api.PUT("/market/settings", { body: body as never })).data!,
    onSuccess: async (saved) => {
      await refresh();
      form.reset(
        marketSettingsFormFrom(saved as MarketSettings, {
          stationId: scopeStationId,
          version: (saved as MarketSettings).version,
        }),
      );
      toast({ tone: "success", title: t("market.settings.saved") });
    },
    onError: (caught) => {
      if (isApiError(caught, "VERSION_CONFLICT")) {
        setConflict(true);
        return;
      }
      setError(errorMessage(caught));
    },
  });

  const submit = form.handleSubmit((values) => {
    setError(null);
    setConflict(false);
    save.mutate(values);
  });
  const needsVerification = watch("needsVerification");

  const field = (
    name: Path<MarketSettingsFormValues>,
    options: { integer?: boolean; suffix?: string } = {},
  ): ReactElement => (
    <FormField
      label={t(`market.settings.fields.${name}`)}
      description={t(`market.settings.hints.${name}`)}
      error={errorAt(errors, name)}
    >
      {(control) => (
        <Input
          {...control}
          {...register(name)}
          {...(options.integer ? { type: "number", inputMode: "numeric" } : { inputMode: "decimal" })}
          disabled={!canManage}
        />
      )}
    </FormField>
  );

  const money = (name: "matchAmountTolerance" | "countApprovalThreshold"): ReactElement => (
    <FormField
      label={t(`market.settings.fields.${name}`)}
      description={t(`market.settings.hints.${name}`)}
      error={errorAt(errors, name)}
    >
      {(control) => (
        <MoneyInput
          {...control}
          value={watch(name)}
          disabled={!canManage}
          onValueChange={(minor) => setValue(name, minor, { shouldDirty: true })}
        />
      )}
    </FormField>
  );

  return (
    <form className="grid gap-4" noValidate onSubmit={(event) => void submit(event)}>
      {conflict ? (
        <Alert tone="warning" title={t("market.settings.conflictTitle")}>
          {t("market.settings.conflictBody")}{" "}
          <Button variant="outline" size="sm" onClick={onReload}>
            {t("market.settings.reload")}
          </Button>
        </Alert>
      ) : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {canManage ? null : <Alert tone="info">{t("market.settings.readOnly")}</Alert>}
      {isDefaultRow ? (
        <Alert tone="info" title={t("market.settings.isDefaultTitle")}>
          {scopeStationId === null ? t("market.settings.isDefaultBody") : t("market.settings.noOverrideBody")}
        </Alert>
      ) : null}
      {needsVerification ? (
        <Alert tone="warning" title={t("market.settings.needsVerificationTitle")}>
          <div className="grid gap-2">
            <p>{t("market.settings.needsVerificationBody")}</p>
            {canManage ? (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  loading={save.isPending}
                  onClick={() => {
                    setValue("needsVerification", false, { shouldDirty: true });
                    void submit();
                  }}
                >
                  {t("market.settings.markVerified")}
                </Button>
              </div>
            ) : null}
          </div>
        </Alert>
      ) : (
        <p>
          <StatusBadge tone="success">{t("market.settings.verified")}</StatusBadge>
        </p>
      )}

      <FormSection title={t("market.settings.sections.stock")}>
        <label className="flex items-start gap-2 text-sm sm:col-span-2">
          <Checkbox className="mt-1" {...register("allowNegativeStock")} disabled={!canManage} />
          <span className="grid gap-0.5">
            <span className="font-medium">{t("market.settings.fields.allowNegativeStock")}</span>
            <span className="text-xs text-muted-foreground">
              {t("market.settings.hints.allowNegativeStock")}
            </span>
          </span>
        </label>
        {field("expiryWarningDays", { integer: true })}
        {field("deadStockDays", { integer: true })}
        {field("salesVelocityDays", { integer: true })}
      </FormSection>

      <FormSection title={t("market.settings.sections.writeOff")}>
        {WRITE_OFF_REASONS.map((reason) => (
          <FormField
            key={reason}
            label={t("market.settings.lossKeyFor", { reason: WRITE_OFF_REASON_LABELS[reason] })}
            error={errorAt(errors, `writeOffLossKeys.${reason}`)}
          >
            {(control) => (
              <Select {...control} {...register(`writeOffLossKeys.${reason}`)} disabled={!canManage}>
                {WRITE_OFF_LOSS_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {t(`market.lossKey.${key}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        ))}
        <label className="flex items-start gap-2 text-sm sm:col-span-2">
          <Checkbox className="mt-1" {...register("writeOffVatReversal")} disabled={!canManage} />
          <span className="grid gap-0.5">
            <span className="font-medium">{t("market.settings.fields.writeOffVatReversal")}</span>
            <span className="text-xs text-muted-foreground">
              {t("market.settings.hints.writeOffVatReversal")}
            </span>
          </span>
        </label>
      </FormSection>

      <FormSection title={t("market.settings.sections.count")}>
        {money("countApprovalThreshold")}
        {field("cycleCountStaleDays", { integer: true })}
      </FormSection>

      <FormSection title={t("market.settings.sections.purchase")}>
        {field("matchQuantityTolerancePercent")}
        {field("matchPriceTolerancePercent")}
        {money("matchAmountTolerance")}
      </FormSection>

      <FormSection title={t("market.settings.sections.pricing")}>
        {field("defaultTargetMarginPercent")}
        {field("priceRoundTo")}
        {field("licenseWarningDays", { integer: true })}
      </FormSection>

      {canManage ? (
        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending}>
            {t("common.save")}
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function FormSection({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <Card className="grid gap-4 p-4 sm:p-5">
      <h2 className="text-base font-semibold">{title}</h2>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </Card>
  );
}

// ── Depolar ──────────────────────────────────────────────────────────────────
function WarehousesTab({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const stations = useCompanyStations(companyId);
  const canManage = useMarketPermissions().canManageCatalog;
  const stationId =
    search.scope && search.scope !== "company" ? search.scope : (stations.data?.[0]?.id ?? null);
  const warehouses = useWarehouses(companyId, stationId, {
    includeInactive: true,
    enabled: stationId !== null,
  });
  const [dialog, setDialog] = useState<{ warehouse: Warehouse | null } | null>(null);

  const update = useMutation({
    mutationFn: async (input: { warehouse: Warehouse; patch: { isDefault?: true; isActive?: boolean } }) =>
      (
        await api.PATCH("/market/warehouses/{id}", {
          params: { path: { id: input.warehouse.id } },
          body: { ...input.patch, version: input.warehouse.version },
        })
      ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("market.warehouses.saved") });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <>
      <ScopePicker
        companyId={companyId}
        value={stationId ?? ""}
        allowCompany={false}
        label={t("market.fields.station")}
        onChange={(next) =>
          void navigate({ search: (previous) => ({ ...previous, scope: next }), replace: true })
        }
      />
      <Card className="grid gap-3 p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="grid gap-1">
            <h2 className="text-base font-semibold">{t("market.warehouses.title")}</h2>
            <p className="text-sm text-muted-foreground">{t("market.warehouses.description")}</p>
          </div>
          {canManage && stationId ? (
            <Button variant="outline" size="sm" onClick={() => setDialog({ warehouse: null })}>
              <Plus aria-hidden="true" />
              {t("market.warehouses.add")}
            </Button>
          ) : null}
        </div>
        {warehouses.error ? (
          <Alert tone="danger">{errorMessage(warehouses.error)}</Alert>
        ) : !warehouses.data ? (
          <SkeletonRows rows={3} />
        ) : warehouses.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("market.warehouses.empty")}</p>
        ) : (
          <TableFrame label={t("market.warehouses.title")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("market.warehouses.fields.code")}</Th>
                <Th>{t("market.warehouses.fields.name")}</Th>
                <Th>{t("market.warehouses.fields.kind")}</Th>
                <Th>{t("common.status")}</Th>
                {canManage ? <Th>{t("common.actions")}</Th> : null}
              </tr>
            </thead>
            <tbody>
              {warehouses.data.map((warehouse) => (
                <tr key={warehouse.id} className="border-t">
                  <td className="px-3 py-2 font-mono">{warehouse.code}</td>
                  <td className="px-3 py-2">{warehouse.name}</td>
                  <td className="px-3 py-2">{WAREHOUSE_KIND_LABELS[warehouse.kind]}</td>
                  <td className="px-3 py-2">
                    <span className="flex flex-wrap gap-1">
                      {warehouse.isDefault ? (
                        <StatusBadge tone="info">{t("market.warehouses.default")}</StatusBadge>
                      ) : null}
                      <StatusBadge tone={warehouse.isActive ? "success" : "neutral"}>
                        {warehouse.isActive ? t("common.active") : t("common.inactive")}
                      </StatusBadge>
                    </span>
                  </td>
                  {canManage ? (
                    <td className="px-3 py-2">
                      <span className="flex flex-wrap gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t("market.warehouses.editLabel", { code: warehouse.code })}
                          onClick={() => setDialog({ warehouse })}
                        >
                          <Pencil aria-hidden="true" />
                          {t("common.edit")}
                        </Button>
                        {/* Yıkama kimyasal deposu (Prompt 18) varsayılan olamaz: kasa satışı ondan düşmemeli. */}
                        {!warehouse.isDefault && warehouse.isActive && warehouse.kind !== "WASH" ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t("market.warehouses.makeDefaultLabel", { code: warehouse.code })}
                            onClick={() => update.mutate({ warehouse, patch: { isDefault: true } })}
                          >
                            <Star aria-hidden="true" />
                            {t("market.warehouses.makeDefault")}
                          </Button>
                        ) : null}
                        {!warehouse.isDefault ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t(
                              warehouse.isActive
                                ? "market.warehouses.deactivateLabel"
                                : "market.warehouses.activateLabel",
                              { code: warehouse.code },
                            )}
                            onClick={() =>
                              update.mutate({ warehouse, patch: { isActive: !warehouse.isActive } })
                            }
                          >
                            {warehouse.isActive ? t("common.deactivate") : t("common.activate")}
                          </Button>
                        ) : null}
                      </span>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </Card>
      {canManage && stationId ? (
        <WarehouseDialog
          open={dialog !== null}
          onOpenChange={(open) => !open && setDialog(null)}
          stationId={stationId}
          warehouse={dialog?.warehouse ?? null}
        />
      ) : null}
    </>
  );
}

function WarehouseDialog({
  open,
  onOpenChange,
  stationId,
  warehouse,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stationId: string;
  warehouse: Warehouse | null;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={warehouse ? t("market.warehouses.editTitle") : t("market.warehouses.addTitle")}
    >
      {open ? (
        <WarehouseForm
          key={warehouse?.id ?? "new"}
          stationId={stationId}
          warehouse={warehouse}
          onDone={() => onOpenChange(false)}
        />
      ) : null}
    </Dialog>
  );
}

function WarehouseForm({
  stationId,
  warehouse,
  onDone,
}: {
  stationId: string;
  warehouse: Warehouse | null;
  onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const [code, setCode] = useState(warehouse?.code ?? "");
  const [name, setName] = useState(warehouse?.name ?? "");
  const [kind, setKind] = useState<WarehouseKind>(warehouse?.kind ?? "BACKROOM");
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () =>
      warehouse
        ? (
            await api.PATCH("/market/warehouses/{id}", {
              params: { path: { id: warehouse.id } },
              body: { name: name.trim(), kind, version: warehouse.version },
            })
          ).data!
        : (
            await api.POST("/market/warehouses", {
              body: {
                stationId,
                code: code.trim(),
                name: name.trim(),
                kind,
                isDefault: isDefault && kind !== "WASH",
              },
            })
          ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("market.warehouses.saved") });
      onDone();
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        if (name.trim() && (warehouse || code.trim())) save.mutate();
      }}
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label={t("market.warehouses.fields.code")}
          description={warehouse ? t("market.warehouses.codeLocked") : t("market.warehouses.codeHint")}
          required
        >
          {(control) => (
            <Input
              {...control}
              className="font-mono uppercase"
              maxLength={20}
              disabled={Boolean(warehouse)}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          )}
        </FormField>
        <FormField label={t("market.warehouses.fields.kind")} required>
          {(control) => (
            <Select
              {...control}
              value={kind}
              onChange={(event) => setKind(event.target.value as WarehouseKind)}
            >
              {WAREHOUSE_KINDS.map((value) => (
                <option key={value} value={value}>
                  {WAREHOUSE_KIND_LABELS[value]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </div>
      <FormField label={t("market.warehouses.fields.name")} required>
        {(control) => (
          <Input
            {...control}
            maxLength={120}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </FormField>
      {warehouse || kind === "WASH" ? null : (
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            className="mt-1"
            checked={isDefault}
            onChange={(event) => setIsDefault(event.target.checked)}
          />
          <span className="grid gap-0.5">
            <span className="font-medium">{t("market.warehouses.isDefault")}</span>
            <span className="text-xs text-muted-foreground">{t("market.warehouses.isDefaultHint")}</span>
          </span>
        </label>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          loading={save.isPending}
          disabled={!name.trim() || (!warehouse && !code.trim())}
        >
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}
